import { Component, inject } from "@angular/core"
import { Timestamp } from "@angular/fire/firestore"
import { FormBuilder, FormGroup, Validators } from "@angular/forms"
import { makeHash } from "@app/consts"
import {
  AuctionCard,
  AuctionV2Card,
  AutoShoppingCard,
  ensureDate,
  EventState,
  MoneyType,
  NONE_PLAYER,
  Pool,
  Question,
  QuestionState,
  QuizEventStream,
  QuizEventTeam,
  Segment,
  SegmentState,
  ShoppingCard,
  ShoppingProductCard,
  StreamEvent,
  StreamEventCard,
  StreamEventObject,
  StreamEventObjectType,
  StreamEventType
} from "@app/consts/types"
import { AuthService } from "@app/shared/services/auth.service"
import {
  ConfigService,
  QUESTION_TYPES
} from "@app/shared/services/config.service"
import { environment } from "@env"

import {
  AuctionFormGroup,
  AuctionV2FormGroup,
  AutoShoppingFormGroup,
  QuestionFormGroup,
  ShoppingFormGroup,
  ShoppingProductFormGroup,
  StreamEventObjectGroup
} from "../quiz/forms"
import { BaseCustomComponent } from "./base.component"

export type PoolGroup = { cash: Pool; coins: Pool }

@Component({
  template: "",
  standalone: false
})
export abstract class QuizFormComponent extends BaseCustomComponent {
  authService = inject(AuthService)
  lockedBrand$ = this.authService.brandLocked$
  forcedBrand: string | null = null
  configService = inject(ConfigService)
  nullableFormbuilder = inject(FormBuilder)
  fb = this.nullableFormbuilder.nonNullable

  questionTypes = QUESTION_TYPES
  quizEventForm = this.quizEventFormGroup()

  get controls() {
    return this.quizEventForm.controls
  }

  quizEventFormGroup(quizEvent?: Partial<StreamEvent>) {
    return this.fb.group({
      id: quizEvent?.id,
      urlFriendlyId:
        quizEvent?.urlFriendlyId || this.getUrlFriendlyId(quizEvent),

      type: quizEvent?.type || "free",
      featured: !!quizEvent?.featured,

      questions: this.questionsFormArray(
        quizEvent?.questions || ([] as Question[])
      ),
      objects: this.objectsFormArray(quizEvent?.objects),

      segments: this.segmentsFormArray(
        quizEvent?.segments || ([] as Segment[])
      ),
      streams: this.streamsFormArray(
        quizEvent?.streams || ([{}] as QuizEventStream[])
      ),

      auctionDefaults: this.auctionDefaultsFormGroup(
        quizEvent?.auctionDefaults
      ),

      team1: this.teamFormGroup(quizEvent?.team1),
      team2: this.teamFormGroup(quizEvent?.team2),

      state: quizEvent?.state || EventState.Undisplayed,
      gameName: quizEvent?.gameName || "",
      subtitle: quizEvent?.subtitle || "",

      eventType:
        quizEvent?.eventType || quizEvent?.category || StreamEventType.Game,
      category: quizEvent?.category || "Esports",
      subCategory: quizEvent?.subCategory || "",
      imageUrl: quizEvent?.imageUrl || "",

      brand: this.forcedBrand || quizEvent?.brand || "",
      brandImgUrl: quizEvent?.brandImgUrl || "",

      closeSettledSeconds: quizEvent?.closeSettledSeconds || 0,

      shopifyStore: this.shopifyStoreFormGroup(quizEvent?.shopifyStore),

      shopifyCollection: this.fb.control(quizEvent?.shopifyCollection || ""),
      shopifyCollections: this.fb.control(quizEvent?.shopifyCollections || []),

      autoshoppingGroup: this.fb.control(quizEvent?.autoshoppingGroup || ""),
      autoauctionGroup: this.fb.control(quizEvent?.autoauctionGroup || ""),

      whitelistIds: this.fb.array(quizEvent?.whitelistIds || ([] as string[])),
      blacklistIds: this.fb.array(quizEvent?.blacklistIds || ([] as string[])),
      blockFromListing: !!quizEvent?.blockFromListing,

      numPlayers: quizEvent?.numPlayers || 0,
      currentHighestStep: quizEvent?.currentHighestStep || 0,

      settleTimes: quizEvent?.settleTimes || {},
      steps: quizEvent?.steps || {},

      shopifyDiscountPerc: quizEvent?.shopifyDiscountPerc || 0,
      affiliate: this.affiliateFormGroup(quizEvent?.affiliate),

      pool: this.poolsFormGroup(quizEvent?.pool),
      managementFee:
        quizEvent?.managementFee ||
        this.configService.config.defaultManagementFee,
      leaderboardIds: this.fb.control([] as string[]),

      chaser: quizEvent?.chaser ?? NONE_PLAYER,
      startsAt: quizEvent?.startsAt,

      limitlessType: quizEvent?.limitlessType || "",

      streamBrand: this.streamBrandFormGroup(quizEvent?.streamBrand)
    })
  }

  questionsFormArray(questions?: Question[]) {
    return this.fb.array(
      (questions || []).map((question) => this.questionFormGroup(question))
    )
  }

  questionFormGroup(question?: Partial<Question>) {
    return this.fb.group({
      ...this.streamEventObjStd(StreamEventObjectType.Question, question),

      text: this.fb.control(question?.text || "", Validators.required),
      options: this.buildStringControls(question?.options, true),
      optionsExtra: this.buildStringControls(
        question?.optionsExtra || [],
        false
      ),
      answers: this.buildStringControls(question?.answers, true),
      state: this.fb.control({
        value: question?.state || QuestionState.Undisplayed,
        disabled: true
      }),
      type: question?.type || this.questionTypes[0],

      rewards: this.questionRewardsGroup(question?.rewards),
      rapid: this.questionRapidGroup(question?.rapid),
      group: question?.group,

      activeUntil: question?.activeUntil,
      limitlessSlug: this.fb.control(question?.limitlessSlug || "")
    }) as FormGroup<QuestionFormGroup>
  }

  buildStringControls(values?: string[], required?: boolean) {
    return this.fb.array(
      (values || []).map((value) => this.fb.control(value)),
      required ? [Validators.required, Validators.minLength(1)] : []
    )
  }

  questionRewardsGroup(rewards?: { steps: number; coins: number }) {
    return this.fb.group({
      steps: rewards?.steps || this.configService.config.trivia.stepValue,
      coins: rewards?.coins || this.configService.config.trivia.coinValue
    })
  }

  questionRapidGroup(rapid?: { seconds: number }) {
    return this.fb.group({
      seconds: rapid?.seconds || 0
    })
  }

  questionId(id: string | undefined | null) {
    return id || makeHash(7)
  }

  streamEventObjStd(
    objectType: StreamEventObjectType,
    obj?: Partial<StreamEventCard> | null
  ) {
    return {
      objectType,
      id: this.questionId(obj?.id),
      undisplayed: this.fb.control(obj?.undisplayed ?? true),
      active: this.fb.control(obj?.active || false)
    }
  }

  shoppingObjStd(
    shopping?: StreamEventCard & { shopifyCollectionId?: string },
    type?: StreamEventObjectType
  ) {
    return {
      ...this.streamEventObjStd(
        type || StreamEventObjectType.Shopping,
        shopping
      ),
      shopifyCollectionId: shopping?.shopifyCollectionId || ""
    }
  }

  shoppingFormGroup(shopping?: ShoppingCard) {
    return this.fb.group(
      this.shoppingObjStd(shopping)
    ) as FormGroup<ShoppingFormGroup>
  }

  shoppingProductFormGroup(product?: ShoppingProductCard) {
    return this.fb.group({
      shopifyProductId: this.fb.control(product?.shopifyProductId || ""),
      ...this.streamEventObjStd(StreamEventObjectType.ShopifyProduct, product)
    }) as FormGroup<ShoppingProductFormGroup>
  }

  segmentFormGroup(segment?: Segment) {
    const group = this.fb.group({
      name: segment?.name,
      state: segment?.state || SegmentState.Incomplete,
      type: segment?.type || "question",
      questions: this.questionsFormArray(
        segment?.questions || ([] as Question[])
      ),
      auctionIds: this.fb.array(segment?.auctionIds || ([] as number[])),
      activeUntil: segment?.activeUntil
    })
    this.subscribeTo(group.controls.state.valueChanges, () =>
      this.mebbeSettleQuiz()
    )
    return group
  }

  async mebbeSettleQuiz() {
    await this.delay(1)

    if (
      this.controls.state.value != EventState.Ended &&
      this.controls.segments.value.every(
        (segment) => segment.state == SegmentState.Settled
      )
    ) {
      this.controls.state.setValue(EventState.Settled)
    }
  }

  delay(ms: number) {
    return new Promise((resolve) => setTimeout(resolve, ms))
  }

  segmentsFormArray(segments?: Segment[]) {
    return this.fb.array(
      (segments || []).map((segment) => this.segmentFormGroup(segment))
    )
  }

  // @LogForm
  streamFormGroup(stream?: QuizEventStream) {
    return this.fb.group({
      url: this.fb.control(stream?.url || ""),
      platform: this.nullableFormbuilder.control(stream?.platform || "")
    })
  }

  // @LogForm
  streamsFormArray(streams?: QuizEventStream[]) {
    return this.fb.array(
      (streams || []).map((stream) => this.streamFormGroup(stream))
    )
  }

  auctionDefaultsFormGroup(defaults?: StreamEvent["auctionDefaults"]) {
    return this.fb.group({
      duration: defaults?.duration || 30,
      popcornRule: this.fb.group({
        withinSeconds: defaults?.popcornRule?.withinSeconds || 10,
        extendBySeconds: defaults?.popcornRule?.extendBySeconds || 10
      }),
      bidRules: this.fb.array(
        (defaults?.bidRules || []).map((rule) =>
          this.fb.group({
            rangeMin: rule.rangeMin,
            rangeMax: rule.rangeMax,
            bump: rule.bump
          })
        )
      )
    })
  }

  shopifyStoreFormGroup(store?: StreamEvent["shopifyStore"]) {
    return this.fb.group({
      domain: this.fb.control(store?.domain || environment.shopifyDomain),
      publicToken: this.fb.control(
        store?.publicToken || environment.shopifyAccessToken
      )
    })
  }

  teamFormGroup(team?: QuizEventTeam) {
    return this.fb.group({
      name: team?.name,
      logoUrl: team?.logoUrl
    })
  }

  moneyFormGroup(money?: MoneyType, currency?: string) {
    return this.fb.group({
      amount: money?.amount || 0,
      currency: money?.currency || currency || "USD"
    })
  }

  poolFormGroup(pool?: Pool, currency?: string) {
    return this.fb.group({
      remaining: this.moneyFormGroup(pool?.remaining, currency),
      total: this.moneyFormGroup(pool?.total, currency)
    })
  }

  poolsFormGroup(pools?: PoolGroup) {
    return this.fb.group({
      cash: this.poolFormGroup(pools?.cash),
      coins: this.poolFormGroup(pools?.coins, "SSC")
    })
  }

  streamBrandFormGroup(streamBrand?: StreamEvent["streamBrand"]) {
    return this.fb.group({
      imgUrl: streamBrand?.imgUrl || "",
      url: streamBrand?.url || ""
    })
  }

  affiliateFormGroup(affiliate: StreamEvent["affiliate"]) {
    return this.fb.group({
      url: affiliate?.url || "",
      imageUrl: affiliate?.imageUrl || "",
      description: affiliate?.description || ""
    })
  }

  auctionFormGroup(auction?: AuctionCard) {
    return this.fb.group({
      ...this.streamEventObjStd(StreamEventObjectType.Auction, auction),
      auctionId: auction?.auctionId || 0
    }) as FormGroup<AuctionFormGroup>
  }

  auctionV2FormGroup(auction?: AuctionV2Card) {
    return this.fb.group({
      ...this.streamEventObjStd(StreamEventObjectType.Auction, auction),
      auctionId: auction?.auctionId || ""
    }) as FormGroup<AuctionV2FormGroup>
  }

  autoShoppingFormGroup(shopping?: AutoShoppingCard) {
    return this.fb.group({
      ...this.shoppingObjStd(shopping, StreamEventObjectType.AutoShopping),
      showXItems: shopping?.showXItems || 0,
      rotateEvery: shopping?.rotateEvery || 0
    }) as FormGroup<AutoShoppingFormGroup>
  }

  emptyObjectFormGroup(object?: StreamEventObject | null) {
    return this.fb.group(
      this.streamEventObjStd(object?.objectType!, object)
    ) as FormGroup<StreamEventObjectGroup>
  }

  formGroupForType(object?: StreamEventObject | null) {
    switch (object?.objectType) {
      case StreamEventObjectType.Shopping:
        return this.shoppingFormGroup(object as ShoppingCard)
      case StreamEventObjectType.ShopifyProduct:
        return this.shoppingProductFormGroup(object as ShoppingProductCard)
      case StreamEventObjectType.AutoShopping:
        return this.autoShoppingFormGroup(object as AutoShoppingCard)
      case StreamEventObjectType.Auction:
        return this.auctionFormGroup(object as AuctionCard)
      case StreamEventObjectType.AuctionV2:
        return this.auctionV2FormGroup(object as AuctionV2Card)
      case StreamEventObjectType.Question:
        return this.questionFormGroup(object as Question)
      default:
        return this.emptyObjectFormGroup(object)
    }
  }

  objectsFormArray(objects: StreamEvent["objects"]) {
    return this.fb.array(
      (objects || ([] as StreamEventObject[])).map((object) =>
        this.formGroupForType(object)
      ) as FormGroup<StreamEventObjectGroup>[]
    )
  }

  objAsQuestion(obj: FormGroup<StreamEventObjectGroup>) {
    return obj.value as Question
  }

  objAsShopping(obj: FormGroup<StreamEventObjectGroup>) {
    return obj.value as ShoppingCard
  }

  objAsShoppingProduct(obj: FormGroup<StreamEventObjectGroup>) {
    return obj.value as ShoppingProductCard
  }

  objAsAuction(obj: FormGroup<StreamEventObjectGroup>) {
    return obj.value as AuctionCard
  }

  getUrlFriendlyId(opts?: Partial<{ gameName: string; startsAt: Timestamp }>) {
    const { gameName, startsAt } = opts || {}
    const date = ensureDate(startsAt)
    let safeGameName = (gameName || "").toLowerCase().replace(/ /g, "-")
    safeGameName = safeGameName.replace(/[^a-z0-9-]/g, "")
    return `${date.getMonth() + 1}-${date.getDate()}-${safeGameName}`
  }

  value() {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { id, leaderboardIds, ...rawQuiz } = this.quizEventForm.getRawValue()

    for (const question of [
      ...(rawQuiz.questions || []),
      ...rawQuiz.segments.flatMap((segment) => segment.questions),
      ...rawQuiz.objects
        .filter((obj) => obj.objectType === StreamEventObjectType.Question)
        .map((obj) => obj as Question)
    ]) {
      question.id = this.questionId(question.id)

      if (!question.rapid?.seconds) {
        delete question.rapid
      }
      question.undisplayed = question.state === QuestionState.Undisplayed
    }

    return { ...rawQuiz, id } as unknown as StreamEvent
  }
}
